import type { FC, ReactNode } from "react";

// Predefined app icon keys
export type PredefinedAppIcon =
  | "spotify"
  | "instacart"
  | "figma"
  | "slack"
  | "github"
  | "notion"
  | "linear"
  | "discord"
  | "stripe";

export type AppWidgetProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** Widget content */
  children: ReactNode;
  /** Optional className for outer container */
  className?: string;
};

/**
 * Generate a deterministic color from a string (app name).
 * Returns an HSL color string.
 */
function generateColorFromString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Use the hash to generate a hue (0-360)
  const hue = Math.abs(hash) % 360;
  // Use fixed saturation and lightness for vibrant, readable colors
  return `hsl(${hue}, 65%, 50%)`;
}

/**
 * Check if a string is a URL (starts with http/https or is a data URI)
 */
function isUrl(str: string): boolean {
  return (
    str.startsWith("http://") ||
    str.startsWith("https://") ||
    str.startsWith("data:")
  );
}

/**
 * Predefined app icons as inline SVGs
 */
const PREDEFINED_ICONS: Record<PredefinedAppIcon, ReactNode> = {
  spotify: (
    <svg viewBox="0 0 24 24" fill="#1DB954" className="app-widget__icon-svg">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
    </svg>
  ),
  instacart: (
    <img
      src="/instacart-icon.png"
      alt="Instacart"
      className="app-widget__icon-svg"
    />
  ),
  figma: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M15.852 8.981h-4.588V0h4.588c2.476 0 4.49 2.014 4.49 4.49s-2.014 4.491-4.49 4.491zM12.735 7.51h3.117c1.665 0 3.019-1.355 3.019-3.019s-1.355-3.019-3.019-3.019h-3.117V7.51zm0 1.471H8.148c-2.476 0-4.49-2.014-4.49-4.49S5.672 0 8.148 0h4.588v8.981zm-4.587-7.51c-1.665 0-3.019 1.355-3.019 3.019s1.355 3.019 3.019 3.019h3.117V1.471H8.148zm4.587 15.019H8.148c-2.476 0-4.49-2.014-4.49-4.49s2.014-4.49 4.49-4.49h4.588v8.98zM8.148 8.981c-1.665 0-3.019 1.355-3.019 3.019s1.355 3.019 3.019 3.019h3.117V8.981H8.148zM8.172 24c-2.489 0-4.515-2.014-4.515-4.49s2.014-4.49 4.49-4.49h4.588v4.441c0 2.503-2.047 4.539-4.563 4.539zm-.024-7.51a3.023 3.023 0 0 0-3.019 3.019c0 1.665 1.365 3.019 3.044 3.019 1.705 0 3.093-1.376 3.093-3.068v-2.97H8.148zm7.704 0h-.098c-2.476 0-4.49-2.014-4.49-4.49s2.014-4.49 4.49-4.49h.098c2.476 0 4.49 2.014 4.49 4.49s-2.014 4.49-4.49 4.49zm-.098-7.509c-1.665 0-3.019 1.355-3.019 3.019s1.355 3.019 3.019 3.019h.098c1.665 0 3.019-1.355 3.019-3.019s-1.355-3.019-3.019-3.019h-.098z" />
    </svg>
  ),
  slack: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  ),
  github: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  ),
  notion: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.448.327s0 .84-1.168.84l-3.22.186c-.094-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z" />
    </svg>
  ),
  linear: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M2.886 10.444a1.2 1.2 0 0 1 0-1.696l7.034-7.035a1.2 1.2 0 0 1 1.697 0l8.076 8.076a9.6 9.6 0 0 1 .118 13.406l-.118.122-1.896-1.897a7.2 7.2 0 0 0-.122-10.042zm.122 10.164 1.896 1.897a7.2 7.2 0 0 0 10.042.122l.122-.122 1.896 1.897a9.6 9.6 0 0 1-13.406.118l-.122-.118-7.034-7.035a1.2 1.2 0 0 1 0-1.697l8.076-8.076a9.6 9.6 0 0 1 13.524 0l.122.122-1.896 1.897a7.2 7.2 0 0 0-10.164-.122l-.122.122z" />
    </svg>
  ),
  discord: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  ),
  stripe: (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="app-widget__icon-svg"
    >
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z" />
    </svg>
  ),
};

/**
 * Predefined icon keys set for type checking
 */
const PREDEFINED_ICON_KEYS = new Set<string>(Object.keys(PREDEFINED_ICONS));

/**
 * Check if a string is a predefined icon key
 */
function isPredefinedIcon(icon: string): icon is PredefinedAppIcon {
  return PREDEFINED_ICON_KEYS.has(icon);
}

/**
 * Letter Avatar component - colored circle with first letter
 */
const LetterAvatar: FC<{ name: string }> = ({ name }) => {
  const letter = name.charAt(0).toUpperCase();
  const bgColor = generateColorFromString(name);

  return (
    <div
      className="app-widget__letter-avatar"
      style={{ backgroundColor: bgColor }}
    >
      {letter}
    </div>
  );
};

/**
 * App Icon component - resolves to predefined, URL, or letter avatar
 */
const AppIcon: FC<{ appName: string; appIcon?: string }> = ({
  appName,
  appIcon,
}) => {
  // No icon provided - use letter avatar
  if (!appIcon) {
    return <LetterAvatar name={appName} />;
  }

  // Check if it's a predefined icon
  if (isPredefinedIcon(appIcon)) {
    return (
      <div className="app-widget__icon app-widget__icon--predefined">
        {PREDEFINED_ICONS[appIcon]}
      </div>
    );
  }

  // Check if it's a URL
  if (isUrl(appIcon)) {
    return (
      <img
        src={appIcon}
        alt={`${appName} icon`}
        className="app-widget__icon app-widget__icon--custom"
      />
    );
  }

  // Fallback to letter avatar if unrecognized
  return <LetterAvatar name={appName} />;
};

/**
 * App Header component - icon + app name
 */
const AppHeader: FC<{ appName: string; appIcon?: string }> = ({
  appName,
  appIcon,
}) => {
  return (
    <div className="app-widget__header">
      <AppIcon appName={appName} appIcon={appIcon} />
      <span className="app-widget__name">{appName}</span>
    </div>
  );
};

/**
 * AppWidget - Generic wrapper for app responses in conversations.
 *
 * Features:
 * - App branding header with icon resolution (predefined, URL, or letter avatar)
 * - Flexible content area for arbitrary children
 * - Themed styling matching ChatGPT's app widgets
 *
 * @example
 * // With predefined icon
 * <AppWidget appName="Spotify" appIcon="spotify">
 *   <CustomContent />
 * </AppWidget>
 *
 * @example
 * // With custom URL icon
 * <AppWidget appName="MyApp" appIcon="https://example.com/icon.png">
 *   <CustomContent />
 * </AppWidget>
 *
 * @example
 * // Auto-generated letter avatar
 * <AppWidget appName="CoolApp">
 *   <CustomContent />
 * </AppWidget>
 */
export const AppWidget: FC<AppWidgetProps> = ({
  appName,
  appIcon,
  children,
  className = "",
}) => {
  const outerClassName = ["app-widget", className].filter(Boolean).join(" ");

  return (
    <div className={outerClassName}>
      <AppHeader appName={appName} appIcon={appIcon} />
      <div className="app-widget__content">{children}</div>
    </div>
  );
};

export { AppHeader, AppIcon, LetterAvatar };
